import React, { useEffect, useLayoutEffect, useState } from "react";
import { Box, Typography } from "../../../design-system";
import logoSite from "../../../../assets/brand/logo_site.svg";

/**
 * Variant L — "Pitch": an interactive investor deck. Navigate with ← → / space, the edge
 * click-zones, or the dots. Every product claim is the shipping product's (ET_BEHAVIOR_SPEC);
 * vision slides are labeled THE VISION; no invented metrics, customers, or benchmarks.
 * Positioning: "the spreadsheet for video" as its own category (not an anti-competitor wedge).
 */

const PINK = "#e61d50";
const AMBER = "#f5a524";
const GREEN = "#22c55e";
const F = "Outfit, sans-serif";
const ANTON = "'Anton', Outfit, sans-serif";
const MONO = "'JetBrains Mono', monospace";
const REDUCED = "@media (prefers-reduced-motion: reduce)";

const injectOnce = () => {
  if (typeof document === "undefined" || document.getElementById("cc-deck-fonts")) return;
  const l = document.createElement("link");
  l.id = "cc-deck-fonts";
  l.rel = "stylesheet";
  l.href =
    "https://fonts.googleapis.com/css2?family=Anton&family=Outfit:wght@400;500;600;700;800&family=JetBrains+Mono:wght@600&display=swap";
  document.head.appendChild(l);
  const s = document.createElement("style");
  s.id = "cc-deck-kf";
  s.textContent = [
    "@keyframes ccDkUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}",
    "@keyframes ccDkPulse{0%,100%{opacity:.55}50%{opacity:1}}",
  ].join("\n");
  document.head.appendChild(s);
};

const up = (delay = 0) => ({
  animation: "ccDkUp .5s cubic-bezier(.2,.7,.2,1) both", animationDelay: `${delay}s`,
  [REDUCED]: { animation: "none" },
});

const chip = (label: string, color: string) => (
  <Box sx={{ ...up(0), display: "inline-block", fontFamily: MONO, fontSize: "10px", letterSpacing: "2px", color, border: `1px solid ${color}55`, borderRadius: "999px", px: "12px", py: "5px", mb: "18px" }}>
    {label}
  </Box>
);

const H = ({ children, size = 64 }: { children: React.ReactNode; size?: number }) => (
  <Typography sx={{ ...up(0.05), fontFamily: ANTON, fontSize: { xs: `${Math.round(size * 0.62)}px`, md: `${size}px` }, lineHeight: 1.02, color: "#f3f4f9", m: 0 }}>
    {children}
  </Typography>
);

const P = ({ children, delay = 0.12 }: { children: React.ReactNode; delay?: number }) => (
  <Typography sx={{ ...up(delay), fontFamily: F, fontSize: { xs: "14.5px", md: "17px" }, lineHeight: 1.7, color: "#b9bfd1", maxWidth: 640, mt: "16px" }}>
    {children}
  </Typography>
);

const Bullets = ({ items, delay = 0.18 }: { items: React.ReactNode[]; delay?: number }) => (
  <Box sx={{ mt: "18px" }}>
    {items.map((it, i) => (
      <Box key={i} sx={{ ...up(delay + i * 0.06), display: "flex", gap: "10px", alignItems: "flex-start", mb: "10px", maxWidth: 640 }}>
        <Typography sx={{ fontFamily: MONO, fontSize: "13px", color: PINK, lineHeight: 1.5 }}>🍒</Typography>
        <Typography sx={{ fontFamily: F, fontSize: { xs: "13.5px", md: "15.5px" }, lineHeight: 1.6, color: "#d5d9e6" }}>{it}</Typography>
      </Box>
    ))}
  </Box>
);

/** CSS mini-spreadsheet motif for the product slide. */
const MiniSheet: React.FC = () => (
  <Box sx={{ ...up(0.25), mt: "22px", maxWidth: 560, borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.12)" }}>
    {[
      ["+", "So every story has its cherries — pick yours.", "2.3s", "87"],
      ["=", "This is the future of video editing.", "1.7s", "74"],
      ["−", "Honestly? The first cut was a mess.", "2.1s", "62"],
      ["+", "The call to action is clear.", "1.5s", "91"],
    ].map(([s, t, d, v], i) => (
      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "10px", px: "12px", py: "8px", background: i % 2 ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.06)" }}>
        <Box sx={{ width: 14, height: 14, borderRadius: "4px", border: "1.5px solid rgba(255,255,255,0.3)", flexShrink: 0 }} />
        <Typography sx={{ fontFamily: MONO, fontSize: "11px", color: s === "+" ? GREEN : s === "−" ? "#f8719d" : "#6aa8ff", width: 12 }}>{s}</Typography>
        <Typography sx={{ fontFamily: F, fontSize: "12.5px", color: "#e6e8f0", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t}</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: "10px", color: "#8d94a8" }}>{d}</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: "10px", color: AMBER }}>{v}</Typography>
      </Box>
    ))}
  </Box>
);

/** Flywheel motif — solid chips ship today, dashed chips are the vision. */
const Flywheel: React.FC = () => (
  <Box sx={{ ...up(0.22), display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", mt: "24px", maxWidth: 680 }}>
    {([
      { t: "Upload anything", vision: false },
      { t: "CherryPick the moments", vision: false },
      { t: "Export 9:16 + captions", vision: false },
      { t: "Clips land in the feed", vision: true },
      { t: "Viewers become creators", vision: true },
    ] as const).map(({ t, vision }, i, arr) => (
      <React.Fragment key={t}>
        <Box sx={{
          fontFamily: F, fontWeight: 600, fontSize: { xs: "11.5px", md: "13px" },
          color: vision ? "#f5d9a8" : "#e6e8f0",
          border: vision ? `1px dashed ${AMBER}66` : "1px solid rgba(255,255,255,0.16)",
          borderRadius: "999px", px: "13px", py: "8px", background: "rgba(255,255,255,0.03)",
        }}>{t}</Box>
        <Typography sx={{ fontFamily: MONO, fontSize: "13px", color: i === arr.length - 1 ? AMBER : "#6b7186", animation: i === arr.length - 1 ? "ccDkPulse 2s infinite" : "none", [REDUCED]: { animation: "none" } }}>
          {i === arr.length - 1 ? "↺" : "→"}
        </Typography>
      </React.Fragment>
    ))}
  </Box>
);

const go = (layout: string) => { window.location.href = `/?layout=${layout}`; };

const SLIDES: { key: string; el: React.ReactNode }[] = [
  {
    key: "cover",
    el: (
      <Box>
        <Box sx={{ ...up(0), display: "flex", alignItems: "center", gap: "10px", mb: "22px" }}>
          <img src={logoSite} alt="" width={30} />
          <Typography sx={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "3px", color: PINK }}>CHERRYCLIPZ · INVESTOR DECK</Typography>
        </Box>
        <H size={96}>TikTok in a box.</H>
        <P>The spreadsheet for video — and the network it can seed. The studio today; the network next.</P>
        <Typography sx={{ ...up(0.3), fontFamily: MONO, fontSize: "11px", color: "#6b7186", mt: "34px", animation: "ccDkPulse 2.4s infinite", [REDUCED]: { animation: "none" } }}>
          → ARROW KEYS, SPACE, OR TAP THE EDGES TO NAVIGATE
        </Typography>
      </Box>
    ),
  },
  {
    key: "problem",
    el: (
      <Box>
        {chip("THE PROBLEM", "#f8719d")}
        <H>Video is where the value is — and the least usable medium on earth.</H>
        <Bullets items={[
          <>Sermons, podcasts, lectures, interviews — hours of gold, locked behind a scrub bar. Finding one moment means re-watching everything.</>,
          <>Meanwhile every social platform starves for good short content — and acquires it the same way: begging creators to perform.</>,
          <>The people with the richest archives are exactly the people short-form tooling ignores.</>,
        ]} />
      </Box>
    ),
  },
  {
    key: "product",
    el: (
      <Box>
        {chip("THE PRODUCT · SHIPPING TODAY", GREEN)}
        <H>A spreadsheet for video.</H>
        <P>Upload anything. Every sentence becomes a row — searchable, filterable, sortable, AI-scored. Editing stops being scrubbing and becomes <em>querying</em>.</P>
        <MiniSheet />
        <Typography sx={{ ...up(0.34), fontFamily: F, fontSize: "12.5px", color: "#8d94a8", mt: "12px" }}>
          Rows in the real format (scores illustrative): sentiment, duration, virality. Trims nudge by 0.1s. Any single clip downloads with one tap.
        </Typography>
      </Box>
    ),
  },
  {
    key: "export",
    el: (
      <Box>
        {chip("THE PRODUCT · SHIPPING TODAY", GREEN)}
        <H>Short-form ready, out of the box.</H>
        <Bullets items={[
          <><b>9:16 vertical export</b> with zoom-to-fill or blurred-frame — rendered at 1080×1920 for Reels, TikTok, and Shorts.</>,
          <><b>Burned-in captions</b> — one word, word groups, or full sentences, sized to taste.</>,
          <><b>Every format the workflow needs:</b> MP4, MP3, GIF, SRT/VTT subtitles, Premiere Pro XML, audiograms — and Super Cut, a montage of only your searched words.</>,
        ]} />
      </Box>
    ),
  },
  {
    key: "ai",
    el: (
      <Box>
        {chip("THE PRODUCT · SHIPPING TODAY", GREEN)}
        <H>Black Cherry AI.</H>
        <Bullets items={[
          <>Chat with your footage: "find the moments about courage" — it selects the rows, in order.</>,
          <>Scores every clip's viral potential 0–100. Flags the filler so the keepers pop. Translates into 47 languages.</>,
          <>Bring-your-own-key architecture: signed-in AI runs in the user's browser on the user's key — <b>our marginal AI cost for the core product is ~zero</b>. (Guest previews use a small, hard-capped server allowance.)</>,
        ]} />
      </Box>
    ),
  },
  {
    key: "insight",
    el: (
      <Box>
        {chip("THE INSIGHT", PINK)}
        <H>The editor is a content refinery.</H>
        <P>Every export that leaves CherryClipz is pre-cut, transcribed, timed, and tied to its creator in our database. That's not just a file — it's <b>feed inventory</b>. People come for the tool; the byproduct is supply.</P>
        <Flywheel />
        <Typography sx={{ ...up(0.3), fontFamily: MONO, fontSize: "9.5px", letterSpacing: "1px", color: "#8a7a5c", mt: "12px" }}>
          SOLID = SHIPS TODAY · DASHED = THE VISION
        </Typography>
      </Box>
    ),
  },
  {
    key: "vision",
    el: (
      <Box>
        {chip("THE VISION", AMBER)}
        <H>Watchers become makers.</H>
        <P>A feed built entirely from what users export — every clip one swipe away, every clip remixable. Because our content is <b>quote-addressable</b>, remixing isn't scrubbing someone's video; it's <em>searching</em> it: CherryPick lines from across creators into a basket, arrange, play — a new reel from other people's best moments, with attribution built in.</P>
        <P delay={0.2}>The unit of content isn't the video, or even the clip. It's the <b>quote</b>. A feed of stitched ideas, not performances.</P>
      </Box>
    ),
  },
  {
    key: "moat",
    el: (
      <Box>
        {chip("WHY WE WIN", PINK)}
        <H>The corpus is the moat.</H>
        <Bullets items={[
          <><b>Quote-addressable content</b>: everything that enters CherryClipz becomes a transcribed, searchable database — few tools force that on the way in.</>,
          <><b>The remix graph (once the feed ships)</b>: every remix will build a graph of who cut what from whom — the part a copycat can't clone.</>,
          <><b>The wedge</b>: long-form communities the big feeds ignore — faith, education, podcasts — huge unclipped archives, quote-sharing cultures, almost no competition for their supply.</>,
          <><b>Honest asymmetry</b>: if the network never ignites, the floor is a revenue-ready SaaS editor (billing already built). Network upside on a tool-business floor.</>,
        ]} />
      </Box>
    ),
  },
  {
    key: "business",
    el: (
      <Box>
        {chip("BUSINESS MODEL", GREEN)}
        <H>Three layers, in order of realism.</H>
        <Bullets items={[
          <><b>1 · SaaS (first):</b> the editor — transcription minutes, exports, AI scoring. Billing is built; this is the honest near-term business.</>,
          <><b>2 · Network take-rate (next):</b> clip licensing, revenue share when your quote powers someone's reel, promoted placement for creators who <em>want</em> to be remixed.</>,
          <><b>3 · Infrastructure (later):</b> the clip-database API — every archive on earth wants to become searchable clips; agents will assemble video by querying quotes.</>,
        ]} />
      </Box>
    ),
  },
  {
    key: "risks",
    el: (
      <Box>
        {chip("RISKS WE RESPECT", "#f8719d")}
        <H>What could kill it — and our answers.</H>
        <Bullets items={[
          <><b>Cold start.</b> A thin feed is a demo, not a product → launch the remix loop inside one community at a time; an auto-clip agent will seed every upload with starter reels. The metric: remixes per upload.</>,
          <><b>Copycats.</b> The mechanism is copyable; the corpus, community, and remix graph aren't → speed into the ignored niches.</>,
          <><b>Rights & moderation.</b> Cross-user remix needs consent and attribution → our share-permission system is the seed; designed in early, not bolted on.</>,
          <><b>Compute costs.</b> Every stitch is real ffmpeg work → metered exports and a scale-hardened worker path before any viral moment.</>,
        ]} />
      </Box>
    ),
  },
  {
    key: "ask",
    el: (
      <Box>
        {chip("SEE IT YOURSELF", PINK)}
        <H>Every demo here is live.</H>
        <P>The editor ships today. The two-phone demo shows the loop. The studio shows where the editing UX goes next. Try them — then let's talk.</P>
        <Box sx={{ ...up(0.2), display: "flex", flexWrap: "wrap", gap: "12px", mt: "26px" }}>
          {[
            { t: "📱 The two-phone demo", l: "K" },
            { t: "🎬 The studio concept", l: "M" },
            { t: "🧮 The real editor", l: "B" },
            { t: "✉️ Join the waitlist", l: "J" },
          ].map((b) => (
            <Box key={b.l} component="button" onClick={(e: React.MouseEvent) => { e.stopPropagation(); go(b.l); }} sx={{
              cursor: "pointer", border: "1px solid rgba(255,255,255,0.22)", borderRadius: "11px", px: "18px", py: "11px",
              fontFamily: F, fontWeight: 700, fontSize: "14px", color: "#f3f4f9", background: "rgba(255,255,255,0.04)",
              "&:hover": { borderColor: PINK, background: `${PINK}22` },
            }}>{b.t}</Box>
          ))}
        </Box>
        <Typography sx={{ ...up(0.3), fontFamily: MONO, fontSize: "12px", color: "#8d94a8", mt: "30px" }}>
          isaac.shrem@gmail.com
        </Typography>
      </Box>
    ),
  },
];

const PitchDeckPage: React.FC = () => {
  const [slide, setSlide] = useState(0);
  const last = SLIDES.length - 1;

  useLayoutEffect(() => { injectOnce(); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return;
      // Space must still activate a focused button/link (a11y) — only hijack it when nothing interactive has focus
      if (e.key === " " && el && (el.tagName === "BUTTON" || el.tagName === "A")) return;
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); setSlide((s) => Math.min(last, s + 1)); }
      if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); setSlide((s) => Math.max(0, s - 1)); }
      if (e.key === "Home") setSlide(0);
      if (e.key === "End") setSlide(last);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [last]);

  return (
    <Box sx={{ position: "relative", minHeight: "calc(100vh - 64px)", overflow: "hidden", background: "radial-gradient(110% 60% at 50% 0%, rgba(230,29,80,0.08), transparent 55%), linear-gradient(180deg,#0e0c12,#08070b)" }}>
      {/* progress */}
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "rgba(255,255,255,0.08)", zIndex: 5 }}>
        <Box sx={{ height: "100%", width: `${((slide + 1) / SLIDES.length) * 100}%`, background: PINK, transition: "width .35s ease" }} />
      </Box>

      {/* slide stage — key remounts so entrance animations replay. Clicking the stage
          navigates (left quarter = back, rest = forward) but yields to buttons/links and
          to text selection, so CTAs stay clickable and the contact email stays copyable. */}
      <Box
        key={SLIDES[slide].key}
        onClick={(e: React.MouseEvent) => {
          const t = e.target as HTMLElement;
          if (t.closest("button") || t.closest("a")) return;
          try { if (window.getSelection()?.toString()) return; } catch (err) { /* ignore */ }
          const x = e.clientX / Math.max(1, window.innerWidth);
          if (x < 0.25) setSlide((s) => Math.max(0, s - 1));
          else setSlide((s) => Math.min(last, s + 1));
        }}
        sx={{ position: "relative", zIndex: 2, maxWidth: 980, mx: "auto", px: { xs: "22px", md: "40px" }, pt: { xs: "48px", md: "84px" }, pb: "120px", minHeight: "calc(100vh - 64px)", display: "flex", flexDirection: "column", justifyContent: "center", cursor: "pointer" }}
      >
        {SLIDES[slide].el}
      </Box>

      {/* controls */}
      <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: "16px", py: "18px", background: "linear-gradient(0deg, rgba(8,7,11,0.9), transparent)" }}>
        <Box component="button" onClick={() => setSlide((s) => Math.max(0, s - 1))} sx={{
          cursor: "pointer", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "999px", width: 36, height: 36,
          color: "#f3f4f9", background: "rgba(255,255,255,0.05)", fontSize: "15px", p: 0, opacity: slide === 0 ? 0.35 : 1,
          "&:hover": { borderColor: PINK },
        }}>←</Box>
        <Box sx={{ display: "flex", gap: "7px", alignItems: "center" }}>
          {SLIDES.map((s, i) => (
            <Box key={s.key} component="button" onClick={() => setSlide(i)} title={s.key} sx={{
              cursor: "pointer", width: i === slide ? 22 : 8, height: 8, borderRadius: "999px", border: "none", p: 0,
              background: i === slide ? PINK : "rgba(255,255,255,0.25)", transition: "all .25s ease",
            }} />
          ))}
        </Box>
        <Box component="button" onClick={() => setSlide((s) => Math.min(last, s + 1))} sx={{
          cursor: "pointer", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "999px", width: 36, height: 36,
          color: "#f3f4f9", background: "rgba(255,255,255,0.05)", fontSize: "15px", p: 0, opacity: slide === last ? 0.35 : 1,
          "&:hover": { borderColor: PINK },
        }}>→</Box>
        <Typography sx={{ fontFamily: MONO, fontSize: "11px", color: "#6b7186", position: "absolute", right: 18 }}>
          {slide + 1} / {SLIDES.length}
        </Typography>
      </Box>
    </Box>
  );
};

export default PitchDeckPage;
